import { Model } from 'backbone';
import _ from 'lodash';
import currency from 'currency.js'

const common_vals = {
	is_coach_only: false,
	by_max: true,
	left_val: {},
	left_is_less: false,
	right_val: {},
	right_is_less: false,
	right_is_empty: true,
	left_val_percent: 0,
	right_val_percent: 0,
};

const _toCurrencyFull = val => {
	if (!_.isNumber(val)) {
		return val;
	}

	const
		currency_str = currency(val, { precision: 0 }).format(),
		is_negative = currency_str[0] === '-';

	return is_negative ? `-$${currency_str.slice(1)}` : `$${currency_str}`;
};

const _getVal = (val, processing = v => v) => processing(val);
const _isLess = (by_max, left, right) => by_max ? (left < right) : (left > right);
const _toCurrency = val => _.currencyFormat(val, '', true);
const _toPercent = val => `${val}%`;
const _getPercents = (by_max, min = 0, max = 0, val = 0) => {
	const
		positive_min = Math.abs(min),
		full_val = positive_min + Math.abs(max);

	val += positive_min;

	let percents = 100 / full_val * val;
	percents = Math.min(percents, 100);

	if (percents < 0) {
		percents = 0;
	}

	return (by_max ? percents : 100 - percents) || 0;
};

const
	TOTAL_ROUNDS_SIZE = parseInt(process.env.TOTAL_ROUNDS_SIZE, 10),
	years_range = _.range((new Date()).getFullYear(), 2013),
	history_stats_options = [
		{ key: 'scores', value: 'SCORE' },
		{ key: 'prices', value: 'PRICE' },
		{ key: 'avg_points', value: 'AVG' },
		{ key: 'cost_divided_by_points', value: '$/POINTS' },
		{ key: 'proj_scores', value: 'PROJ SCORE' },
	];

export class Compare extends Model {
	get defaults() {
		return {
			round_id: 1,
			compare: [],
			colors: ['#B60820', '#2BB353', '#3491A8', '#F5A623'],
			is_coach_stats: false,
			years_range,
			selected_year: _.first(years_range),
			history_stats_options,
			selected_history_stat: _.first(history_stats_options).key,
			has_assistant_coach: false,
			stats: [
				{
					...common_vals,
					is_coach_only: true,
					title: () => `Round ${this.get('round_id')} Projected`,
					max_val: 80,
					min_val: 0,
					key: 'stats.proj_score',
				},
				{
					...common_vals,
					is_coach_only: true,
					title: 'Break Even',
					by_max: false,
					max_val: 80,
					min_val: -40,
					key: 'stats.break_even',
				},
				{
					...common_vals,
					is_coach_only: true,
					title: 'Projected Price Change',
					max_val: 100000, // $100k
					min_val: -100000, // $100k
					key: 'stats.proj_price_change_next_round',
					processing: _toCurrency
				},
				{
					...common_vals,
					is_coach_only: true,
					title: 'Consistency Rating',
					max_val: 100,
					min_val: 0,
					key: 'stats.consistency',
				},
				{
					...common_vals,
					title: 'Dollars Per Point',
					by_max: false,
					max_val: 30000, // $30k
					min_val: 1000, // $1k
					key: 'stats.cost_divided_by_points',
					processing: _toCurrencyFull
				},
				{
					...common_vals,
					title: 'Average Points',
					max_val: 80,
					min_val: 0,
					key: 'stats.avg_points',
				},
				{
					...common_vals,
					title: 'Last 3 Average',
					max_val: 80,
					min_val: 0,
					key: 'stats.last_3_avg',
				},
				{
					...common_vals,
					title: '% Owned',
					max_val: 100,
					min_val: 0,
					key: 'stats.owned_by',
					processing: _toPercent
				}
			]
		}
	}

	getEmptyHistory(year){
		const history = {};

		_.set(history, year, {
			scores: [],
			prices: [],
			avg_points: [],
			proj_scores: [],
			cost_divided_by_points: [],
		});

		return history;
	}

	getRoundsRange() {
		return _.range(1, TOTAL_ROUNDS_SIZE + 1);
	}

	getChartDataSet() {
		const
			stat = this.get('selected_history_stat'),
			year = this.get('selected_year'),
			players = this.get('compare'),
			colors = this.get('colors');

		return players.map((player, index) => ({
			data: _.result(player, `history[${year}][${stat}]`),
			borderColor: colors[index],
			pointBackgroundColor: colors[index],
			borderWidth: 1,
			fill: false,
			spanGaps: true
		}));
	}

	calcPlayersSimpleStats() {
		const
			[player_1, player_2] = this.get('compare'),
			stats = _.map(this.defaults.stats, stat => {
				const
					{ by_max, processing, key, min_val, max_val } = stat,
					right_is_empty = !player_2,
					left_val = _.result(player_1, key, 0),
					right_val = _.result(player_2, key, 0),
					left_is_less = !right_is_empty && _isLess(by_max, left_val, right_val),
					right_is_less = !right_is_empty && _isLess(by_max, right_val, left_val);

				return ({
					...stat,
					right_is_empty,
					left_is_less,
					right_is_less,
					left_val: _getVal(left_val, processing),
					right_val: _getVal(right_val, processing),
					left_val_percent: _getPercents(by_max, min_val, max_val, left_val),
					right_val_percent: (
						!right_is_empty && _getPercents(by_max, min_val, max_val, right_val) || 0
					),
				})
			});

		this.set({ stats });

		return this;
	}

	calcPlayersCoachStats({ db } = {}) {
		if (!this.get('has_assistant_coach')) {
			return this;
		}

		const
			players = this.get('compare'),
			current_year = this.get('selected_year'),
			rounds = this.getRoundsRange();

		_.each(players, player => {
			player.history = this.getEmptyHistory(current_year);

			_.each(rounds, round_id => {
				const
					stats = player.history[current_year],
					person = db ? _.result(db, player.id, {}) : player,
					round_score = _.result(person, `stats.scores[${round_id}]`),
					round_price = _.result(person, `stats.prices[${round_id}]`),
					played_rounds = _.range(1, round_id + 1).map(
						round_id => _.result(person, `stats.scores[${round_id}]`)
					).filter(_.identity),
					avg = round_score ? _.sum(played_rounds) / _.size(played_rounds) : undefined,
					cost_divided_by_points = _.toInteger(round_price / round_score) || undefined;

				stats.scores.push(round_score);
				stats.prices.push(round_price);
				stats.avg_points.push(avg);
				stats.cost_divided_by_points.push(cost_divided_by_points);
				stats.proj_scores.push(_.result(person, `stats.proj_scores[${round_id}]`));
			})
		});

		return this;
	}

	addPlayer({ player }, silent = true) {
		this.set({
			compare: [
				...this.get('compare'),
				{ ...player, stats_table: {} }
			]
		}, { silent });

		return this;
	}

	removePlayer({ player_id }, silent = true) {
		const
			compare = this.get('compare'),
			index = _.findIndex(compare, player => player.id === player_id);

		compare.splice(index, 1);

		this.set({ compare }, { silent });

		return this;
	}

	removePlayers({ players }, silent = true) {
		_.each(players, player => this.removePlayer({ player_id: player.id }, silent));

		return this;
	}
}

export default Compare;